import * as React from "react"
import {
  GalleryVerticalEndIcon
} from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import type { HarnessCustomer } from "@/api"
import { EntitySelector } from "@/app/sidebar/entity-selector/EntitySelector"
import { NavUser } from "@/app/sidebar/user-menu/UserMenu"
import { getResolutionCenterUrl } from "@/common/keycloak/keycloak"
import { setSelectedCustomerId } from "@/store/billing-customer"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/common/elements/sidebar"
import { usePermissions } from "@/common/hooks/use-permissions"
import { buildNavigationMenu } from "@/config/navigationMenu"
import { routes } from "@/config/routes"

const user = {
  name: "CloudFit Admin",
  email: "admin@cloudfitsoftware.com",
  avatar: "/avatars/cloudfit-admin.png",
} as const

type AppSidebarProps = React.ComponentProps<typeof Sidebar> & {
  customers: HarnessCustomer[]
  user?: {
    name: string
    email: string
    avatar?: string
  }
  onLogout?: () => void
}

export function AppSidebar({ customers: sidebarCustomers, onLogout, user: sidebarUser = user, ...props }: AppSidebarProps) {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { isMobile, setOpenMobile } = useSidebar()
  const [resolutionCenterUrl, setResolutionCenterUrl] = React.useState<string | null>(null)
  const selectedCustomerId = useAppSelector((state) => state.billingCustomer.selectedCustomerId)
  const { hasPagePermission } = usePermissions()
  const menu = React.useMemo(() => buildNavigationMenu(resolutionCenterUrl ?? undefined), [resolutionCenterUrl])

  const customers = React.useMemo(() => {
    return sidebarCustomers.map((customer) => ({
      id: customer.id,
      name: customer.name,
      logo: GalleryVerticalEndIcon
    }))
  }, [sidebarCustomers])

  React.useEffect(() => {
    let cancelled = false

    async function hydrateResolutionCenterUrl() {
      try {
        const configuredUrl = await getResolutionCenterUrl()

        if (!cancelled) {
          setResolutionCenterUrl(configuredUrl)
        }
      } catch {
        if (!cancelled) {
          setResolutionCenterUrl(null)
        }
      }
    }

    void hydrateResolutionCenterUrl()

    return () => {
      cancelled = true
    }
  }, [])

  const isCustomerSelectorDisabled = customers.length === 0

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <EntitySelector
          entities={customers}
          title="Companies"
          isDisabled={isCustomerSelectorDisabled}
          fallbackLabel="No customers available"
          selectedEntityId={selectedCustomerId}
          onEntityChange={(customer) => dispatch(setSelectedCustomerId(customer.id))}
          onEntitySelected={() => {
            if (isMobile) {
              setOpenMobile(false)
            }
          }}
        />
      </SidebarHeader>
      <SidebarContent>
        {menu.sections.map((section) => {
          const availableMenuItems = section.items.filter((item) => item.type === "external" || hasPagePermission(item.page))
          if (availableMenuItems.length === 0) {
            return null
          }
          return (
            <SidebarGroup key={section.displayName}>
              <SidebarGroupLabel>{section.displayName}</SidebarGroupLabel>
              <SidebarMenu>
                {availableMenuItems.map((item) => {
                  const itemKey = item.type === "internal"
                    ? item.page
                    : `${item.displayName}:${item.href}`

                  if (item.type === "internal") {
                    return (
                      <SidebarMenuItem key={itemKey}>
                        <SidebarMenuButton asChild tooltip={item.displayName} isActive={location.pathname === routes[item.page].href}>
                          <Link to={routes[item.page].href}>
                            <item.icon />
                            <span>{item.displayName}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  }

                  return (
                    <SidebarMenuItem key={itemKey}>
                      <SidebarMenuButton asChild tooltip={item.displayName} isActive={false}>
                        <a
                          href={item.href}
                          target={item.openInNewTab ? "_blank" : undefined}
                          rel={item.openInNewTab ? "noopener noreferrer" : undefined}
                        >
                          <item.icon />
                          <span>{item.displayName}</span>
                        </a>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
              </SidebarMenu>
            </SidebarGroup>
          )
        })}
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={sidebarUser} onLogout={onLogout} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
